/**
 * ChatAvaDisplay — extracted from ConversationsChat.js (Session 4 refactor).
 * Jul 2026
 *
 * Purely presentational. Renders the AvA avatar + status + interaction-mode
 * toggle. Two variants share this component so we don't duplicate props /
 * styling:
 *   • Desktop panel (`lg:flex w-96`)  — full portrait, name+status,
 *     3-way mode selector, AvA reference-content dock.
 *   • Mobile prominent bar (`lg:hidden`) — compact avatar + name + status
 *     + icon-only mode toggle.
 *
 * Behaviour is byte-identical to the pre-extraction inline JSX. The parent
 * still owns interaction-mode state, `showAvaLightbox`, and the underlying
 * `AvAContextDisplay` message stream.
 */
import React from 'react';
import { MessageCircle, Headphones, MessageSquare, Share2 } from 'lucide-react';
import { IdleLoopVideo } from './IdleLoopVideo';
import { MomentVideoOverlay } from './MomentVideoOverlay';
import { NeuralOrb } from '../NeuralOrb';
import AvAContextDisplay from './AvAContextDisplay';
import MoodAuraRing from '../engagement/MoodAuraRing';

// Reusable AvA avatar tile — the shared circle used by both variants.
// Kept private to this file because it's only meaningful in this context.
const AvaTile = ({ selectedAva, idleLoopUrl, momentVideoUrl, onMomentEnd, onMomentStarted, onMomentProgress, momentMuted, onClick, className, testId, videoTestId }) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
    aria-label={`Enlarge ${selectedAva.name} animation`}
    className={className}
    data-testid={testId}
  >
    {idleLoopUrl ? (
      <IdleLoopVideo
        src={idleLoopUrl}
        poster={selectedAva.avatar_url}
        className="w-full h-full object-cover"
        testId={videoTestId}
      />
    ) : (
      <img
        src={selectedAva.avatar_url}
        alt={selectedAva.name}
        className="w-full h-full object-cover"
        data-testid="ava-static-image"
      />
    )}
    {/* Lip-synced moment clip (greeting / reveal / farewell) plays over the idle loop */}
    <MomentVideoOverlay videoUrl={momentVideoUrl} onEnd={onMomentEnd} onStarted={onMomentStarted} onProgress={onMomentProgress} muted={momentMuted} />
  </div>
);

// Only ONE of the two breakpoint variants may carry the clip's audio —
// CSS `hidden` does not silence a playing <video>, so without this the
// desktop + mobile twins both play and the voice echoes.
const isDesktopViewport = () =>
  typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches;

const MODES = [
  { id: 'text', icon: MessageCircle, label: 'Text' },
  { id: 'voice', icon: Headphones, label: 'Voice' },
  { id: 'text_voice', icon: MessageSquare, label: 'Both' },
];

export const ChatAvaDisplay = ({
  selectedAva,
  idleLoopUrl,
  momentVideoUrl,
  onMomentEnd,
  onMomentStarted,
  onMomentProgress,
  isThinking = false,
  onShareAva,
  isSpeaking,
  sending,
  interactionMode,
  setInteractionMode,
  setShowAvaLightbox,
  messages,
}) => {
  const statusLabel = isSpeaking ? 'Speaking' : sending ? 'Thinking' : 'Ready';
  const statusLabelMobile = isSpeaking ? 'Speaking' : sending ? 'Thinking' : 'Ready to chat';
  const statusActive = isSpeaking || sending;

  return (
    <>
      {/* Desktop version */}
      <div className="hidden lg:flex w-72 xl:w-80 2xl:w-96 flex-shrink-0 p-4 xl:p-6 bg-gradient-to-b from-slate-50 to-slate-100 border-r border-gray-200 flex-col items-center">
        <MoodAuraRing
          className="rounded-2xl"
          extra={isSpeaking ? (
            <div className="flex items-end gap-[3px] px-2.5 py-1 bg-black/50 backdrop-blur-sm rounded-full" data-testid="speaking-bars">
              {[0, 1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="w-[3px] bg-green-400 rounded-full animate-pulse"
                  style={{ height: `${8 + Math.random() * 10}px`, animationDelay: `${i * 0.15}s` }}
                />
              ))}
            </div>
          ) : null}
        >
          <AvaTile
            selectedAva={selectedAva}
            idleLoopUrl={idleLoopUrl}
            momentVideoUrl={momentVideoUrl}
            onMomentEnd={onMomentEnd}
            onMomentStarted={onMomentStarted}
            onMomentProgress={onMomentProgress}
            momentMuted={!isDesktopViewport()}
            onClick={() => setShowAvaLightbox(true)}
            className="relative w-52 h-52 xl:w-60 xl:h-60 2xl:w-72 2xl:h-72 rounded-2xl overflow-hidden shadow-2xl ring-4 ring-white cursor-zoom-in focus:outline-none focus:ring-purple-400"
            testId="ava-display"
            videoTestId="ava-idle-loop-video"
          />
        </MoodAuraRing>

        <div className="mt-6 text-center">
          <h3 className="text-2xl font-bold text-gray-900">{selectedAva.name}</h3>
          {/* Dedicated status slot — fixed height so Ready ↔ Orb swap never
              shifts the layout (founder spec). */}
          <div className="h-[100px] flex flex-col items-center justify-center" data-testid="ava-status-slot">
            {isThinking ? (
              <div className="flex flex-col items-center" data-testid="ava-thinking-orb-desktop">
                <NeuralOrb size="md" />
                <p className="mt-1 text-sm font-medium text-gray-500" data-testid="ava-thinking-label">
                  {selectedAva.name} is thinking...
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2">
                <div className={`w-3 h-3 rounded-full ${statusActive ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                <span className="text-base text-gray-600 font-medium">{statusLabel}</span>
              </div>
            )}
          </div>
          {onShareAva && (
            <button
              type="button"
              onClick={onShareAva}
              className="mt-3 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-white border border-purple-200 text-purple-600 text-xs font-semibold hover:bg-purple-50 transition-colors"
              data-testid="share-ava-btn"
            >
              <Share2 size={13} /> Share my AvA
            </button>
          )}
        </div>

        {/* Interaction Mode Selector — Desktop */}
        <div className="mt-4 w-full" data-testid="interaction-mode-selector">
          <p className="text-sm text-gray-500 text-center mb-3 font-medium">Interaction Mode</p>
          <div className="flex gap-2">
            {MODES.map((m) => {
              const MIcon = m.icon;
              const active = interactionMode === m.id;
              return (
                <button
                  key={m.id}
                  onClick={() => setInteractionMode(m.id)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all border-2 ${
                    active
                      ? 'bg-purple-600 text-white border-purple-600 shadow-md'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-purple-300 hover:text-purple-600'
                  }`}
                  data-testid={m.id === 'text_voice' ? 'mode-text-voice' : `mode-${m.id}`}
                >
                  <MIcon size={16} /> {m.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* AvA Reference Content box — Feb 2026.
            Surfaces any URL / image AvA mentions in its replies,
            with expand / save / download / share / delete tools. */}
        <AvAContextDisplay messages={messages} avaName={selectedAva.name} />
      </div>

      {/* Mobile/Tablet AvA prominent bar — stacks above chat log per S-026 */}
      <div className="lg:hidden flex items-center gap-3 px-3 py-3 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setShowAvaLightbox(true)}
          className="relative w-32 h-32 sm:w-40 sm:h-40 rounded-2xl overflow-hidden ring-2 ring-white shadow-md flex-shrink-0 cursor-zoom-in focus:outline-none focus:ring-4 focus:ring-purple-400 transition"
          aria-label={`Enlarge ${selectedAva.name} animation`}
          data-testid="ava-display-mobile"
        >
          {idleLoopUrl ? (
            <IdleLoopVideo
              src={idleLoopUrl}
              poster={selectedAva.avatar_url}
              className="w-full h-full object-cover"
              testId="ava-idle-loop-video-mobile"
            />
          ) : (
            <img src={selectedAva.avatar_url} alt={selectedAva.name} className="w-full h-full object-cover" />
          )}
          <MomentVideoOverlay videoUrl={momentVideoUrl} onEnd={onMomentEnd} onStarted={onMomentStarted} onProgress={onMomentProgress} muted={isDesktopViewport()} testId="moment-video-overlay-mobile" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-base font-bold text-gray-900 truncate">{selectedAva.name}</p>
          {/* Fixed-height status slot (mobile) — Ready ↔ Orb swap, no shift */}
          <div className="h-[48px] flex items-center" data-testid="ava-status-slot-mobile">
            {isThinking ? (
              <div className="flex items-center gap-2" data-testid="ava-thinking-orb-mobile">
                <NeuralOrb size="sm" />
                <span className="text-xs font-medium text-gray-500 whitespace-nowrap">{selectedAva.name} is thinking...</span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <div className={`w-2 h-2 rounded-full ${statusActive ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                <span className="text-xs text-gray-600">{statusLabelMobile}</span>
              </div>
            )}
          </div>
        </div>
        {/* Mobile mode toggle */}
        <div className="flex gap-1">
          {MODES.map((m) => {
            const MIcon = m.icon;
            const active = interactionMode === m.id;
            return (
              <button
                key={m.id}
                onClick={() => setInteractionMode(m.id)}
                className={`p-1.5 rounded-lg transition-all ${
                  active
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-gray-400 border border-gray-200'
                }`}
                data-testid={`mode-${m.id}-mobile`}
              >
                <MIcon size={14} />
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default ChatAvaDisplay;
